<template>
  <section class="your-stores">
    <div
      v-if="$root.isLoggedIn && loading"
      class="your-stores__loader"
    >
      <spinner />
    </div>
    <div v-if="$root.isLoggedIn && !loading">
      <store-list
        :stores="savedStores"
        heading="Sparade butiker"
        :current-store-id="currentStoreId"
        @selectedStore="emitSelectedStore"
      />
      <!--
      <store-list
        v-if="visitedStores.length > 0"
        :stores="visitedStores"
        :current-store-id="currentStoreId"
        heading="Besökta butiker"
        @selectedStore="emitSelectedStore"
      />-->

      <searchNotification
        v-if="notice"
        :message="noticeMessage"
        :heading="noticeHeading"
        :is-error-message="isErrorMessage"
      />
    </div>

    <div
      v-if="!$root.isLoggedIn"
      class="your-stores__not-logged-in"
    >
      <div class="your-stores__text-wrapper">
        <h3 class="your-stores__heading">
          Logga in och välj butik
        </h3>
        <p class="your-stores__text">
          Se dina sparade butiker och välj eller ändra vilken som ska vara vald på hemsidan, t ex för erbjudanden och e-handeln.
        </p>
      </div>
      <button
        class="button--48 your-stores__button"
        type="button"
        @click="handleLoginClick"
      >
        Logga in
      </button>
    </div>
  </section>
</template>

<script>
  import searchNotification from "@/vue/components/common/searchNotification"
  import { mapActions } from 'vuex'
  import { service } from '@/services/'
  import storeList from './storeList'
  import spinner from './spinner'

  export default {
    name: 'YourStoresTab',
    components: {
      searchNotification,
      storeList,
      spinner
    },
    props: {
      currentStoreId: {
        type: String,
        default: ""
      }
    },
    data () {
      return {
        loading: true,
        notice: false,
        isErrorMessage: true,
        noticeMessage: "",
        noticeHeading: "",
        savedStores: [],
        visitedStores: []
      }
    },
    mounted () {
      if (this.$root.isLoggedIn) {
        this.getStores()
      }
    },
    methods: {
      ...mapActions(['getPublicAccessToken']),
      async getStores () {
        this.loading = true
        this.notice = false

        try {
          const serviceResponse = await service.getFavoriteStores({
            url: this.$root.envConfig.customerStoresApiBaseUrl,
            publicAccessToken: await this.getPublicAccessToken()
          })
          this.loading = false

          if(serviceResponse.length > 0) {
            this.updateResults(serviceResponse)
          } else {
            this.notice = true
            this.isErrorMessage = false
            this.noticeHeading = "Inga sparade butiker"
            this.noticeMessage = "Du kan spara vilka som är dina butiker i ICA-appen."
          }
        } catch (error) {
          this.loading = false
          this.notice = true
          this.isErrorMessage = true
          console.error("failure", error)
        }
      },
      capitalizeObjectProps (obj) {
        // Makes { name: foo, id: bar } to { Name: foo, Id: bar}
        let entries = Object.entries(obj)
        let capsEntries = entries.map((entry) => [entry[0][0].toUpperCase() + entry[0].slice(1), entry[1]])
        return Object.fromEntries(capsEntries)
      },
      updateResults (stores) {
        // BE API gives us props in lowercase while store search uses Kebabcase, we must convert to the later
        for (const store of stores){
          this.savedStores.push(this.capitalizeObjectProps(store))
        }
      },
      async emitSelectedStore (store) {
        // Since we only get a part of the store result from favorite stores we need to get all data from store search api
        this.loading = true
        this.notice = false
        const useaccountnr = !!store.AccountNumber // Cast to boolean
        let storeId = useaccountnr ? store.AccountNumber : store.Id

        try {
          const storeResponse = await service.searchStoreById({
            url: this.$root.envConfig.baseUrl,
            storeId,
            useaccountnr
          })
          this.loading = false

          if(storeResponse.Stats.TotalHits && storeResponse.Documents) {
            this.$emit('selectedStore', storeResponse.Documents[0])
          } else {
            this.notice = true
            this.isErrorMessage = true
            this.noticeMessage = "Butiken kunde tyvärr inte sparas."
          }
        } catch (error) {
          this.loading = false
          this.notice = true
          this.isErrorMessage = true
          this.noticeMessage = "Butiken kunde tyvärr inte sparas."
          console.error("failure", error)
        }
      },
      createReturnUrl () {
        const queryChar = window.location.search ? '&' : '?'
        return `${window.location.href}${queryChar}openStorePickerModal=true`
      },
      handleLoginClick () {
        /* Since we need to trigger a login from a modal these are the steps neccessary:
         * DESKTOP
         * 1. Close the store modal
         * 2. Open the login modal
         * 3. Prevent reload after login (reloadOnLogin: false)
         * 4. Add a query param (openStorePickerModal=true)
         * 5. Reload page to get all logged in states
         * 5. Open store modal when openStorePickerModal query is true (from my-store.vue), now as logged in
         * MOBILE
         * 1. Close the store modal
         * 2. Open the login modal
         * 3. Send returnUrl with query param (openStorePickerModal=true) to forceUserLogin which will reload that page when logged in. */

        if (this.$ICALogin) {
          this.$emit('toggleModal', false)
          const options = {
            reloadOnLogin: false,
            returnUrl: this.createReturnUrl(),
            onLoginCompleted: this.onLoginCompleted
          }
          this.$ICALogin.forceUserLogin(this.$root.envConfig.loginEndpointUrl, () => {}, options)
        }
      },
      onLoginCompleted () {
        window.location = this.createReturnUrl()
      }
    }
  }
</script>

<style lang="scss">
  .your-stores {
    height: 100%;

    .store-list + .store-list,
    .store-list + .no-result {
      margin-top: 1.5rem;
    }

    &__loader {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 20rem;
    }

    &__not-logged-in {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      white-space: normal;
    }

    &__text-wrapper {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
      justify-content: center;
    }

    &__heading {
      @include font(rubrik, 3, "bold");

      margin: 0 0 0.5rem;
    }

    &__text {
      line-height: 1.5rem;
      max-width: 80%;
      margin: 0 auto;
    }

    &__button {
      margin-top: 1rem;
    }

    @include media-breakpoint-up(md) {
      &__heading {
        @include font(rubrik, 3, "bold");
      }

      &__button {
        width: 230px;
      }

      &__text {
        max-width: 295px;
        margin-bottom: 0.5rem;
      }

      &__not-logged-in {
        align-items: center;
        margin: 15% auto 0 auto;
      }
    }
  }
</style>
